import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CaretDown, CaretRight, Sparkle, FloppyDisk, Spinner } from "@phosphor-icons/react";

// Inline editor for the RSAW evidence-table columns (Version / Document Date /
// Relevant Pages / Description of Applicability) shown per evidence item. Values flow
// straight into the populated RSAW evidence table.
export default function EvidenceDetails({ evidence, requirementId, onSaved, readOnly = false }) {
  const { aiEnabled, aiConfidentialBlocked } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [describing, setDescribing] = useState(false);
  const [form, setForm] = useState({
    version: evidence.version || "",
    document_date: evidence.document_date || "",
    pages: evidence.pages || "",
    description: evidence.description || "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/evidence/${evidence.id}`, form);
      toast.success("Evidence details saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const aiDescribe = async () => {
    setDescribing(true);
    try {
      const qs = requirementId ? `?requirement_id=${requirementId}` : "";
      const { data } = await api.post(`/evidence/${evidence.id}/ai-describe${qs}`);
      setForm((f) => ({ ...f, description: data.description || "" }));
      toast.success("AI wrote a description of applicability — review & save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDescribing(false);
    }
  };

  const field = "w-full rounded-none border border-border px-2 py-1 text-xs bg-background";
  return (
    <div className="border border-border/70 bg-secondary/10" data-testid={`evidence-details-${evidence.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`evidence-details-toggle-${evidence.id}`}
        className="w-full flex items-center justify-between px-2.5 py-1.5 text-left"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium truncate">
          {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
          <span className="truncate">✓ {evidence.name}</span>
        </span>
        <span className="text-[11px] text-muted-foreground font-mono shrink-0">
          {form.pages ? `p.${form.pages}` : ""}{form.description ? " · described" : ""}
        </span>
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="overline text-muted-foreground">Version</label>
              <input value={form.version} onChange={set("version")} disabled={readOnly}
                     data-testid={`evidence-version-${evidence.id}`} className={field} placeholder="e.g. 2.0" />
            </div>
            <div>
              <label className="overline text-muted-foreground">Document Date</label>
              <input value={form.document_date} onChange={set("document_date")} disabled={readOnly} type="date"
                     data-testid={`evidence-date-${evidence.id}`} className={field} placeholder="YYYY-MM-DD" />
            </div>
            <div>
              <label className="overline text-muted-foreground">Relevant Pages</label>
              <input value={form.pages} onChange={set("pages")} disabled={readOnly}
                     data-testid={`evidence-pages-${evidence.id}`} className={field} placeholder="e.g. 1-4" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="overline text-muted-foreground">Description of Applicability</label>
              {aiEnabled && !aiConfidentialBlocked && !readOnly && (
                <button type="button" onClick={aiDescribe} disabled={describing}
                        data-testid={`evidence-ai-describe-${evidence.id}`}
                        className="flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline disabled:opacity-50">
                  {describing ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} />}
                  {describing ? "Writing…" : "AI describe"}
                </button>
              )}
            </div>
            <textarea value={form.description} onChange={set("description")} disabled={readOnly} rows={4}
                      data-testid={`evidence-description-${evidence.id}`} className={field}
                      placeholder="How this evidence demonstrates compliance…" />
          </div>
          {!readOnly && (
            <button type="button" onClick={save} disabled={saving}
                    data-testid={`evidence-save-${evidence.id}`}
                    className="flex items-center gap-1.5 border border-black bg-primary text-primary-foreground px-2.5 py-1 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50">
              {saving ? <Spinner size={12} className="animate-spin" /> : <FloppyDisk size={12} />} Save details
            </button>
          )}
        </div>
      )}
    </div>
  );
}
